import { useState, useCallback } from 'react'

import { useQueryClient, type DefaultError, type FetchQueryOptions, type QueryKey } from '@tanstack/react-query'

type QueryFnFactory<
  TParams extends unknown[],
  TQueryFnData,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = (...params: TParams) => FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>

export const useFetchQuery = <TParams extends unknown[], TData>(queryFactory: QueryFnFactory<TParams, TData>) => {
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()

  const fetchQuery = useCallback(
    async (...params: TParams): Promise<TData> => {
      setIsLoading(true)
      try {
        const options = queryFactory(...params)
        const result = await queryClient.fetchQuery(options)
        return result
      } finally {
        setIsLoading(false)
      }
    },
    [queryClient, queryFactory],
  )

  return {
    isLoading,
    fetchQuery,
  }
}
